// Batch-3 SEO location pages (2026-07-22).
// Complex names come from the CAPTCHA-free eCourts state/district/complex hierarchy
// harvested into district-complexes.json. These pages guide users to public court
// records; they do not change the separate court-search admission registry.
import type { LocationData } from './locations';

const ECOURTS_DISTRICT_PORTAL = 'https://districts.ecourts.gov.in';

const CASE_TYPES = [
  'Civil Suits & Property Disputes',
  'Criminal Cases & Sessions Trials',
  'Bail Applications',
  'Family & Matrimonial Matters',
  'Motor Accident Claims (MACT)',
  'Cheque Bounce Cases (Section 138 NI Act)',
];

interface Batch3Seed {
  slug: string;
  cityName: string;
  stateName: string;
  districtName: string;
  highCourtName: string;
  highlights: string[];
  aboutCourt: string;
  seoTitle: string;
  aliases?: string[];
  courtName?: string;
  jurisdiction?: string;
  searchHint?: string;
}

function buildDistrictLocation(seed: Batch3Seed): LocationData {
  const complexSummary = seed.highlights.join(', ');
  return {
    slug: seed.slug,
    cityName: seed.cityName,
    stateName: seed.stateName,
    courtType: 'District Court',
    description: `Check ${seed.cityName} court case status by CNR, case number or party name. Follow hearing dates, case-status changes and published orders from the official ${seed.districtName} district court record, with alerts available after you add the correct matter.`,
    highlights: seed.highlights,
    courtInfo: {
      name: seed.courtName ?? `${seed.cityName} District Courts`,
      jurisdiction: seed.jurisdiction ?? `${seed.districtName} District, ${seed.stateName}`,
      website: ECOURTS_DISTRICT_PORTAL,
    },
    seoTitle: seed.seoTitle,
    seoDescription: `Check ${seed.cityName} court case status by CNR, case number or party name. Follow official hearing dates, status changes and orders from ${seed.districtName} district courts.`,
    seoH1: `${seed.cityName} District Court Case Status`,
    seoH1Subtitle: `${seed.districtName} district judiciary — case search and tracking`,
    altNames: seed.aliases ?? [`${seed.cityName} Court Case Status`, `${seed.cityName} District Court CNR Status`],
    searchQueries: [
      `${seed.cityName.toLowerCase()} court case status`,
      `${seed.cityName.toLowerCase()} district court case status`,
      `district court ${seed.cityName.toLowerCase()} case status`,
    ],
    smartSearchHint: seed.searchHint ?? `${seed.cityName} ${seed.districtName} District Court ${seed.stateName}`,
    aboutCourt: seed.aboutCourt,
    caseTypes: CASE_TYPES,
    commonProblems: `A one-time result does not tell you when the next date, case stage or published order changes. India Case Status helps you identify the correct ${seed.cityName} matter from the official court record, save it to a dashboard and receive alerts when tracked information changes, while retaining the court and CNR context needed to verify the result.`,
    extraFaqs: [
      {
        question: `How do I check ${seed.cityName} court case status by CNR?`,
        answer: `Enter the 16-character CNR number to identify the matter on the official court record. After confirming the court and party details, add the case to your dashboard to follow its next date, status and published orders.`,
      },
      {
        question: `Which High Court hears appeals from ${seed.cityName}?`,
        answer: `Appeals and revisions from the relevant ${seed.districtName} district courts ordinarily lie to the ${seed.highCourtName}, subject to the case type and applicable procedural rules.`,
      },
      {
        question: `Which court complexes are included on the ${seed.cityName} page?`,
        answer: `The official eCourts hierarchy identifies these relevant complexes: ${complexSummary}. Select the court shown on your filing papers or CNR result before saving the matter.`,
      },
      {
        question: `How do I find the next hearing date for a ${seed.cityName} case?`,
        answer: `Search by CNR, case number or party name and verify the matching court record. The current next date appears with the matter, and a saved case can alert you when the official record changes.`,
      },
    ],
  };
}

export const locationsBatch3: LocationData[] = [
  buildDistrictLocation({
    slug: 'kalyan-court-case-status',
    cityName: 'Kalyan',
    stateName: 'Maharashtra',
    districtName: 'Thane',
    highCourtName: 'Bombay High Court',
    courtName: 'Kalyan Courts, Thane Judicial District',
    jurisdiction: 'Kalyan court complexes within Thane Judicial District, Maharashtra',
    searchHint: 'Kalyan Addl Sessions Railway Maharashtra',
    highlights: [
      'Kalyan, District and Addl Sessions',
      'Kalyan, Railway Court',
      'Kalyan courts within the Thane judicial district',
    ],
    seoTitle: 'Kalyan Court Case Status by CNR, Case Number & Party Name',
    aliases: ['Kalyan District Court Case Status', 'Kalyan Court CNR Status', 'Thane Kalyan Court Case Status'],
    aboutCourt:
      'Kalyan is served by court complexes recorded within the Thane judicial district of Maharashtra. The official eCourts hierarchy identifies Kalyan, District and Addl Sessions and Kalyan, Railway Court as Kalyan-specific complexes; this page is scoped to those Kalyan records rather than every court in Thane district. The courts deal with assigned civil and criminal matters, including sessions work, bail applications and railway-related cases according to their jurisdiction. Appeals and revisions from the district judiciary lie to the Bombay High Court, subject to the nature of the proceeding. A CNR number remains the clearest identifier because it ties a case to its exact court establishment and record.',
  }),
  buildDistrictLocation({
    slug: 'salem-court-case-status',
    cityName: 'Salem',
    stateName: 'Tamil Nadu',
    districtName: 'Salem',
    highCourtName: 'Madras High Court',
    highlights: ['Combined Courts, Salem', 'Combined Courts, Mettur', 'Combined Courts, Attur', 'Combined Courts, Sankari'],
    seoTitle: 'Salem Court Case Status by CNR, Case Number & Party Name',
    aboutCourt:
      'The Salem district judiciary serves litigants through the Combined Courts, Salem and outlying establishments across the district. The official eCourts hierarchy also records combined court complexes at Mettur, Attur, Sankari and Omalur, along with District Munsif-cum-Judicial Magistrate courts at Vazhapadi, Edappadi and Yercaud. These establishments handle civil suits, criminal complaints and trials, bail work, family disputes, accident claims and cheque-bounce matters according to their assigned jurisdiction. Appeals and revisions lie to the Madras High Court, subject to the proceeding involved. Searching with the CNR number helps distinguish matters filed at similarly named courts in different Salem complexes.',
  }),
  buildDistrictLocation({
    slug: 'ambala-court-case-status',
    cityName: 'Ambala',
    stateName: 'Haryana',
    districtName: 'Ambala',
    highCourtName: 'Punjab and Haryana High Court',
    highlights: ['District Court, Ambala', 'Judicial Complex, Naraingarh', 'Railway Complex, Ambala Cantt'],
    seoTitle: 'Ambala Court Case Status by CNR, Case Number & Party Name',
    aboutCourt:
      'The District Court, Ambala is the principal establishment of the Ambala district judiciary in Haryana. The official eCourts hierarchy also lists the Judicial Complex, Naraingarh and the Railway Complex, Ambala Cantt, so users should match the establishment shown on their filing papers before selecting a result. Courts in the district deal with civil disputes, criminal and sessions matters, bail applications, matrimonial proceedings, accident claims and cheque-bounce complaints within their assigned jurisdiction. Appeals and revisions ordinarily lie to the Punjab and Haryana High Court at Chandigarh. A CNR search provides the most precise route to the correct establishment, case number and current proceeding details.',
  }),
  buildDistrictLocation({
    slug: 'bathinda-court-case-status',
    cityName: 'Bathinda',
    stateName: 'Punjab',
    districtName: 'Bathinda',
    highCourtName: 'Punjab and Haryana High Court',
    highlights: ['District Court, Bathinda', 'Judicial Complex, Phul', 'Judicial Complex, Talwandi Sabo'],
    seoTitle: 'Bathinda Court Case Status by CNR, Case Number & Party Name',
    aboutCourt:
      'The District Court, Bathinda heads the district judiciary for Bathinda in Punjab. The official eCourts court-complex list also identifies the Judicial Complex, Phul and the Judicial Complex, Talwandi Sabo as establishments in the district. Together these courts hear civil and property disputes, criminal cases and sessions trials, bail applications, family proceedings, motor-accident claims and complaints under Section 138 of the Negotiable Instruments Act according to allocation and territorial jurisdiction. Appeals and revisions ordinarily lie to the Punjab and Haryana High Court at Chandigarh. The CNR number is the safest search key because it identifies the specific establishment and matter even when party names or case-number formats vary.',
  }),
  buildDistrictLocation({
    slug: 'ajmer-court-case-status',
    cityName: 'Ajmer',
    stateName: 'Rajasthan',
    districtName: 'Ajmer',
    highCourtName: 'Rajasthan High Court',
    highlights: ['Ajmer District Court Complex', 'Ajmer Railway Court Complex', 'Kishangarh Court Complex', 'Kekri Court Complex'],
    seoTitle: 'Ajmer Court Case Status by CNR, Case Number & Party Name',
    aboutCourt:
      'The Ajmer District Court Complex is the main establishment of the Ajmer district judiciary in Rajasthan. The official eCourts hierarchy also records the Ajmer Railway Court Complex and courts at Kishangarh, Kekri, Sarwar, Pushkar and Nasirabad, as well as a Gram Nyayalaya at Pisangan. These establishments handle civil litigation, criminal and sessions matters, bail applications, family disputes, accident claims and cheque-bounce cases according to the court concerned. Appeals and revisions ordinarily lie to the Rajasthan High Court. Because several complexes serve the district, the CNR number is especially useful for identifying the correct establishment and avoiding a similarly numbered matter at another Ajmer court.',
  }),
  buildDistrictLocation({
    slug: 'ujjain-court-case-status',
    cityName: 'Ujjain',
    stateName: 'Madhya Pradesh',
    districtName: 'Ujjain',
    highCourtName: 'Madhya Pradesh High Court',
    highlights: ['District & Sessions Court, Ujjain', 'Family Court, Ujjain', 'Civil Court, Nagda', 'Civil Court, Badnagar'],
    seoTitle: 'Ujjain Court Case Status by CNR, Case Number & Party Name',
    aboutCourt:
      'The District and Sessions Court at Ujjain, located in the Kothi Palace area on Dewas Road, is the principal establishment of the Ujjain district judiciary in Madhya Pradesh. The official eCourts hierarchy separately lists the Family Court at Ujjain and civil court establishments at Nagda, Badnagar, Khachraud, Tarana and Mahidpur. These courts handle civil suits, criminal and sessions work, bail applications, matrimonial proceedings, accident claims and cheque-bounce complaints according to subject and territorial jurisdiction. Appeals and revisions lie to the Madhya Pradesh High Court as provided by law. A CNR search connects the user to the exact Ujjain establishment and is more reliable than relying on party names alone.',
  }),
  buildDistrictLocation({
    slug: 'darbhanga-court-case-status',
    cityName: 'Darbhanga',
    stateName: 'Bihar',
    districtName: 'Darbhanga',
    highCourtName: 'Patna High Court',
    highlights: ['Civil Court Darbhanga', 'Civil Court, Benipur', 'Biraul Court'],
    seoTitle: 'Darbhanga Court Case Status by CNR, Case Number & Party Name',
    aboutCourt:
      'Civil Court Darbhanga is the principal court complex serving the Darbhanga district judiciary in Bihar. The official eCourts hierarchy also records Civil Court, Benipur and Biraul Court, so the district record spans more than the headquarters complex. The courts deal with civil and property disputes, criminal matters, bail proceedings, family cases, motor-accident claims and cheque-bounce complaints according to their assigned powers and territorial jurisdiction. Appeals and revisions from the district judiciary ordinarily lie to the Patna High Court. Searching with the CNR number is the most dependable way to connect a matter to Darbhanga, Benipur or Biraul and confirm the correct case history and next date.',
  }),
  buildDistrictLocation({
    slug: 'purnea-court-case-status',
    cityName: 'Purnea',
    stateName: 'Bihar',
    districtName: 'Purnea',
    highCourtName: 'Patna High Court',
    highlights: ['Civil Court Purnea', 'Banmankhi Court', 'Dhamdaha Sub Division', 'Baisi Sub Division'],
    seoTitle: 'Purnea Court Case Status by CNR, Case Number & Party Name',
    aliases: ['Purnia Court Case Status', 'Purnea District Court CNR Status', 'Civil Court Purnea Case Status'],
    aboutCourt:
      'Civil Court Purnea is the headquarters establishment for the Purnea district judiciary in Bihar. The official eCourts hierarchy also identifies Banmankhi Court and court establishments for the Dhamdaha and Baisi subdivisions. These courts hear civil and property disputes, criminal matters, bail applications, family proceedings, motor-accident claims and cheque-bounce complaints within the jurisdiction assigned to each establishment. Appeals and revisions ordinarily lie to the Patna High Court. Purnea is also commonly written as Purnia, which can complicate a name search; the CNR number avoids that ambiguity by identifying the exact case and court establishment on the official record.',
  }),
  buildDistrictLocation({
    slug: 'nanded-court-case-status',
    cityName: 'Nanded',
    stateName: 'Maharashtra',
    districtName: 'Nanded',
    highCourtName: 'Bombay High Court',
    highlights: ['Nanded, District and Sessions Court', 'Bhokar, Civil and Criminal Court', 'Biloli, District and Sessions Court', 'Degloor, Civil and Criminal Court'],
    seoTitle: 'Nanded Court Case Status by CNR, Case Number & Party Name',
    aboutCourt:
      'Nanded, District and Sessions Court is the principal establishment of the Nanded district judiciary in Maharashtra. The official eCourts hierarchy records a broad network of additional establishments, including civil and criminal courts at Bhokar, Degloor, Kandhar, Kinwat, Dharmabad, Mukhed and other centres, together with district and sessions work at Biloli. These courts handle civil suits, criminal proceedings, bail, family disputes, accident claims and cheque-bounce complaints according to allocation and territorial jurisdiction. Appeals and revisions lie to the Bombay High Court, subject to the applicable bench and case type. With many establishments in the district, a CNR search is the most reliable way to locate the exact Nanded record.',
  }),
  buildDistrictLocation({
    slug: 'azamgarh-court-case-status',
    cityName: 'Azamgarh',
    stateName: 'Uttar Pradesh',
    districtName: 'Azamgarh',
    highCourtName: 'Allahabad High Court',
    highlights: ['Azamgarh District Court Complex', 'Gram Nyayalaya, Phulpur', 'Gram Nyayalaya, Lalganj', 'Gram Nyayalaya, Mehnagar'],
    seoTitle: 'Azamgarh Court Case Status by CNR, Case Number & Party Name',
    aboutCourt:
      'The Azamgarh District Court Complex is the principal establishment of the Azamgarh district judiciary in Uttar Pradesh. The official eCourts hierarchy also records Gram Nyayalayas serving Burhanpur, Phulpur, Lalganj and Mehnagar, extending local access beyond the headquarters complex. Courts in the district deal with civil and property disputes, criminal and sessions cases, bail applications, family proceedings, accident claims and cheque-bounce complaints within their respective jurisdiction. Appeals and revisions ordinarily lie to the Allahabad High Court. The CNR number is the best identifier for confirming whether a result belongs to the main Azamgarh complex or a listed Gram Nyayalaya and for following the correct case record.',
  }),
];
